export const CTA_DEFAULT_FONT_SIZE = 16
export const CTA_MIN_FONT_SIZE = 12

export const SHOP_PAY_DEFAULT_FONT_SIZE = 16
export const SHOP_PAY_MIN_FONT_SIZE = 12
export const SHOP_PAY_DEFAULT_LOGO_HEIGHT = 30
export const SHOP_PAY_DEFAULT_LOGO_WIDTH = 80
export const SHOP_PAY_MIN_LOGO_HEIGHT = 15
export const SHOP_PAY_LOGO_HEIGHT_RATIO =
    SHOP_PAY_DEFAULT_LOGO_HEIGHT / SHOP_PAY_DEFAULT_FONT_SIZE
export const SHOP_PAY_LOGO_WIDTH_RATIO =
    SHOP_PAY_DEFAULT_LOGO_WIDTH / SHOP_PAY_DEFAULT_LOGO_HEIGHT

export function getShopPayLogoWidth(height: number): number {
    return height * SHOP_PAY_LOGO_WIDTH_RATIO
}

function parseCssPixels(value: string): number {
    const parsed = Number.parseFloat(value)
    return Number.isNaN(parsed) ? 0 : parsed
}

export function getInnerWidth(element: HTMLElement): number {
    const style = getComputedStyle(element)
    const paddingLeft = parseCssPixels(style.paddingLeft)
    const paddingRight = parseCssPixels(style.paddingRight)
    return element.clientWidth - paddingLeft - paddingRight
}

export function findFontSizeToFit(opts: {
    innerWidth: number
    measureAtFontSize: (fontSize: number) => number
    defaultFontSize?: number
    minFontSize?: number
}): number {
    const defaultFontSize = opts.defaultFontSize ?? CTA_DEFAULT_FONT_SIZE
    const minFontSize = opts.minFontSize ?? CTA_MIN_FONT_SIZE

    if (opts.innerWidth <= 0) {
        return defaultFontSize
    }

    if (opts.measureAtFontSize(defaultFontSize) <= opts.innerWidth) {
        return defaultFontSize
    }

    for (let size = defaultFontSize - 1; size >= minFontSize; size--) {
        if (opts.measureAtFontSize(size) <= opts.innerWidth) {
            return size
        }
    }

    return minFontSize
}

export type ShopPayFitResult = {
    fontSize: number
    logoHeight: number
    logoOnly: boolean
}

export function findShopPayFit(opts: {
    innerWidth: number
    measure: (state: {
        fontSize: number
        logoHeight: number
        logoOnly: boolean
    }) => number
    defaultFontSize?: number
    minFontSize?: number
    defaultLogoHeight?: number
    minLogoHeight?: number
}): ShopPayFitResult {
    const defaultFontSize = opts.defaultFontSize ?? SHOP_PAY_DEFAULT_FONT_SIZE
    const minFontSize = opts.minFontSize ?? SHOP_PAY_MIN_FONT_SIZE
    const defaultLogoHeight =
        opts.defaultLogoHeight ?? SHOP_PAY_DEFAULT_LOGO_HEIGHT
    const minLogoHeight = opts.minLogoHeight ?? SHOP_PAY_MIN_LOGO_HEIGHT

    if (opts.innerWidth <= 0) {
        return {
            fontSize: defaultFontSize,
            logoHeight: defaultLogoHeight,
            logoOnly: false,
        }
    }

    const defaultState = {
        fontSize: defaultFontSize,
        logoHeight: defaultLogoHeight,
        logoOnly: false,
    }

    if (opts.measure(defaultState) <= opts.innerWidth) {
        return defaultState
    }

    for (
        let fontSize = defaultFontSize - 1;
        fontSize >= minFontSize;
        fontSize--
    ) {
        const state = {
            fontSize,
            logoHeight: fontSize * SHOP_PAY_LOGO_HEIGHT_RATIO,
            logoOnly: false,
        }
        if (opts.measure(state) <= opts.innerWidth) {
            return state
        }
    }

    const scaledLogoAtMinFont = minFontSize * SHOP_PAY_LOGO_HEIGHT_RATIO
    if (scaledLogoAtMinFont % 1 !== 0) {
        const fractionalState = {
            fontSize: minFontSize,
            logoHeight: scaledLogoAtMinFont,
            logoOnly: false,
        }
        if (opts.measure(fractionalState) <= opts.innerWidth) {
            return fractionalState
        }
    }

    for (
        let logoHeight = Math.floor(scaledLogoAtMinFont);
        logoHeight >= minLogoHeight;
        logoHeight--
    ) {
        const state = {
            fontSize: minFontSize,
            logoHeight,
            logoOnly: false,
        }
        if (opts.measure(state) <= opts.innerWidth) {
            return state
        }
    }

    for (
        let logoHeight = defaultLogoHeight;
        logoHeight >= minLogoHeight;
        logoHeight--
    ) {
        const state = {
            fontSize: minFontSize,
            logoHeight,
            logoOnly: true,
        }
        if (opts.measure(state) <= opts.innerWidth) {
            return state
        }
    }

    return {
        fontSize: minFontSize,
        logoHeight: minLogoHeight,
        logoOnly: true,
    }
}
